<template>
  <div class="employee-panel">
    <div v-if="loading" class="loading-grid">
      <el-skeleton
        v-for="n in 3"
        :key="n"
        animated
        :rows="5"
        class="section-card skeleton-card"
      />
    </div>

    <el-empty
      v-else-if="!items.length"
      :description="emptyDescription"
      :image-size="96"
      class="section-card empty-card"
    />

    <div v-else class="cards-grid" :class="{ 'home-cards-grid': isHomeScope }">
      <el-card
        v-for="item in items"
        :key="item.id"
        class="section-card case-card"
        :class="{ 'home-case-card': isHomeScope }"
        shadow="never"
      >
        <template v-if="isHomeScope">
          <div class="home-case-shell">
            <div class="home-case-main">
              <div class="case-head home-case-head">
                <div>
                  <p class="case-id">{{ `Case # ${item.id}` }}</p>
                </div>
              </div>

              <div class="home-meta-row">
                <div class="home-meta-item home-meta-item-title">
                  <span class="home-meta-label">Protocol Title</span>
                  <strong
                    class="home-meta-value"
                    :title="item.protocolTitle || item.submission?.protocolTitle || 'Not provided'"
                  >
                    {{ item.protocolTitle || item.submission?.protocolTitle || 'Not provided' }}
                  </strong>
                </div>
                <div class="home-meta-item">
                  <span class="home-meta-label">Submitted</span>
                  <strong class="home-meta-value">{{ formatDate(item.submittedAt) || 'Not available' }}</strong>
                </div>
              </div>
            </div>

            <div class="case-actions home-case-actions">
              <el-tooltip
                v-if="profileIncomplete"
                content="Complete your employee profile to assign cases."
                placement="top"
              >
                <div class="disabled-button-wrapper">
                  <el-button
                    type="primary"
                    class="case-action-btn action-assign"
                    disabled
                  >
                    Assign to myself
                  </el-button>
                </div>
              </el-tooltip>
              <el-button
                v-else
                type="primary"
                class="case-action-btn action-assign"
                :loading="loadingById[item.id] === 'assign'"
                @click="assignCase(item)"
              >
                Assign to myself
              </el-button>
              <el-tooltip
                v-if="profileIncomplete"
                content="Complete your employee profile to mark cases as 'not interested'."
                placement="top"
              >
                <div class="disabled-button-wrapper">
                  <el-button
                    class="case-action-btn action-hide"
                    disabled
                  >
                    Not interested
                  </el-button>
                </div>
              </el-tooltip>
              <el-button
                v-else
                class="case-action-btn action-hide"
                :loading="loadingById[item.id] === 'hide'"
                @click="hideCase(item)"
              >
                Not interested
              </el-button>
              <el-button class="case-action-btn action-details" @click="openDetails(item)">
                Case details
              </el-button>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="case-head">
            <div>
              <p class="case-id">{{ item.id }}</p>
              <h3>{{ item.submissionName || item.submission?.submissionName || 'Untitled case' }}</h3>
            </div>
            <el-tag :type="statusType(item.status)" round effect="light">
              {{ item.status || 'Unknown' }}
            </el-tag>
          </div>

          <div class="case-layout">
            <div class="case-copy">
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">Protocol #</span>
                  <strong>{{ item.protocolNumber || item.submission?.protocolNumber || 'Not provided' }}</strong>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Submission Type</span>
                  <strong>{{ item.submissionType || item.submission?.submissionType || 'Not provided' }}</strong>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Sponsor</span>
                  <strong>{{ item.sponsor || item.submission?.sponsor || 'Not provided' }}</strong>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Customer</span>
                  <strong>{{ item.createdByEmail || item.createdBy || 'Not provided' }}</strong>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Assigned To</span>
                  <strong>{{ item.assignedToName || item.assignedToEmail || 'Unassigned' }}</strong>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Updated</span>
                  <strong>{{ formatDate(item.updatedAt) || 'Not available' }}</strong>
                </div>
              </div>
            </div>

            <div class="case-actions">
              <template v-if="isActiveScope">
                <el-upload
                  class="upload-trigger"
                  action="#"
                  :show-file-list="false"
                  :http-request="(option) => uploadDocument(item, option)"
                  :disabled="uploadingById[item.id]"
                  multiple
                >
                  <el-button type="primary" plain :loading="uploadingById[item.id]">
                    Upload document
                  </el-button>
                </el-upload>

                <div class="status-editor">
                  <el-select v-model="statusDrafts[item.id]" placeholder="Select status">
                    <el-option
                      v-for="status in statusOptions"
                      :key="status"
                      :label="status"
                      :value="status"
                    />
                  </el-select>
                  <el-button
                    type="success"
                    :disabled="!statusDrafts[item.id] || statusDrafts[item.id] === item.status"
                    :loading="loadingById[item.id] === 'status'"
                    @click="saveStatus(item)"
                  >
                    Save status
                  </el-button>
                </div>
              </template>

              <el-button class="case-action-btn action-details" @click="openDetails(item)">
                Case details
              </el-button>
            </div>
          </div>
        </template>
      </el-card>
    </div>

    <CaseDetailsDialog
      v-if="showDetails"
      :caseId="selectedCaseId"
      :initial-case="selectedCase"
      :visible="showDetails"
      @close="showDetails = false; selectedCaseId = null; selectedCase = null"
    />
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import CaseDetailsDialog from '@/components/CaseDetailsDialog.vue'
import { watchList } from '@/services/requests'
import {
  assignCaseToCurrentEmployee,
  markCaseNotInterested,
  updateCaseStatus,
  uploadCaseAttachment,
} from '@/services/cases'

const STATUS_OPTIONS = Object.freeze([
  'Submitted',
  'In-Progress',
  'In-Review',
  'Review-Done',
  'Completed',
  'Rejected',
])

export default {
  name: 'EmployeeCasesPanel',
  components: { CaseDetailsDialog },
  props: {
    scope: {
      type: String,
      required: true,
    },
    currentUser: {
      type: Object,
      default: null,
    },
    employeeProfile: {
      type: Object,
      default: null,
    },
    profileIncomplete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      items: [],
      loading: true,
      unsub: null,
      showDetails: false,
      selectedCaseId: null,
      selectedCase: null,
      statusDrafts: {},
      loadingById: {},
      uploadingById: {},
      statusOptions: STATUS_OPTIONS,
    }
  },
  computed: {
    isHomeScope() {
      return this.scope === 'employee-home'
    },
    isActiveScope() {
      return this.scope === 'employee-active'
    },
    emptyDescription() {
      if (this.scope === 'employee-home') return 'No submitted cases are waiting right now.'
      if (this.scope === 'employee-active') return 'You do not have any active requests assigned to you.'
      return 'You do not have any completed or closed requests yet.'
    },
  },
  watch: {
    scope: 'subscribe',
    currentUser: 'subscribe',
  },
  mounted() {
    this.subscribe()
  },
  beforeUnmount() {
    this.unsub && this.unsub()
  },
  methods: {
    subscribe() {
      this.unsub && this.unsub()

      const uid = this.currentUser?.uid
      if (!uid) {
        this.items = []
        this.loading = false
        return
      }

      this.loading = true
      this.unsub = watchList({ scope: this.scope, uid }, (rows) => {
        this.items = rows
        this.statusDrafts = rows.reduce((drafts, row) => {
          drafts[row.id] = this.statusDrafts[row.id] || row.status || 'Submitted'
          return drafts
        }, {})
        this.loading = false
      })
    },
    setLoading(caseId, action, value) {
      this.loadingById = {
        ...this.loadingById,
        [caseId]: value ? action : '',
      }
    },
    setUploading(caseId, value) {
      this.uploadingById = {
        ...this.uploadingById,
        [caseId]: value,
      }
    },
    statusType(status) {
      const normalized = String(status || '').trim().toLowerCase()
      if (normalized === 'submitted') return 'primary'
      if (normalized === 'in-progress' || normalized === 'in progress') return 'warning'
      if (normalized === 'in-review' || normalized === 'in review') return 'info'
      if (normalized.includes('review') && normalized.includes('done')) return 'success'
      if (normalized === 'completed' || normalized === 'closed') return 'success'
      if (normalized === 'rejected') return 'danger'
      return 'info'
    },
    formatDate(ts) {
      if (!ts) return ''
      try {
        const ms = ts.toMillis ? ts.toMillis() : (ts.seconds || 0) * 1000
        return new Date(ms).toLocaleString()
      } catch {
        return ''
      }
    },
    openDetails(item) {
      this.selectedCase = item
      this.selectedCaseId = item?.id || null
      this.showDetails = true
    },
    async assignCase(item) {
      const user = this.currentUser
      if (!user?.uid) return

      this.setLoading(item.id, 'assign', true)
      try {
        await assignCaseToCurrentEmployee({
          caseId: item.id,
          user,
          profile: this.employeeProfile,
        })
        ElMessage({ type: 'success', message: `${item.id} was assigned to you.`, showClose: true })
      } catch (error) {
        ElMessage({
          type: 'error',
          message: error?.message || 'Unable to assign this case right now.',
          showClose: true,
          duration: 5000,
        })
      } finally {
        this.setLoading(item.id, 'assign', false)
      }
    },
    async hideCase(item) {
      const user = this.currentUser
      if (!user?.uid) return

      this.setLoading(item.id, 'hide', true)
      try {
        await markCaseNotInterested({ caseId: item.id, user })
        ElMessage({ type: 'success', message: `${item.id} was removed from your queue.`, showClose: true })
      } catch (error) {
        ElMessage({
          type: 'error',
          message: error?.message || 'Unable to hide this case right now.',
          showClose: true,
          duration: 5000,
        })
      } finally {
        this.setLoading(item.id, 'hide', false)
      }
    },
    async saveStatus(item) {
      const user = this.currentUser
      if (!user?.uid) return

      this.setLoading(item.id, 'status', true)
      try {
        await updateCaseStatus({
          caseId: item.id,
          newStatus: this.statusDrafts[item.id],
          user,
          profile: this.employeeProfile,
        })
        ElMessage({ type: 'success', message: `Status updated for ${item.id}.`, showClose: true })
      } catch (error) {
        ElMessage({
          type: 'error',
          message: error?.message || 'Unable to update this case status right now.',
          showClose: true,
          duration: 5000,
        })
      } finally {
        this.setLoading(item.id, 'status', false)
      }
    },
    async uploadDocument(item, option) {
      const user = this.currentUser
      if (!user?.uid) {
        option.onError && option.onError(new Error('Not signed in'))
        return
      }

      this.setUploading(item.id, true)
      try {
        await uploadCaseAttachment({
          caseId: item.id,
          file: option.file,
          user,
        })
        option.onSuccess && option.onSuccess({}, option.file)
        ElMessage({ type: 'success', message: `Document uploaded for ${item.id}.`, showClose: true })
      } catch (error) {
        option.onError && option.onError(error)
        ElMessage({
          type: 'error',
          message: error?.message || 'Unable to upload the document right now.',
          showClose: true,
          duration: 5000,
        })
      } finally {
        this.setUploading(item.id, false)
      }
    },
  },
}
</script>

<style scoped>
.employee-panel {
  display: grid;
  gap: 16px;
}

.loading-grid,
.cards-grid {
  display: grid;
  gap: 16px;
}

.home-cards-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.skeleton-card,
.empty-card,
.case-card {
  border-radius: 22px;
}

.case-card {
  background:
    radial-gradient(circle at top right, rgba(11, 108, 255, 0.08), transparent 32%),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.home-case-card :deep(.el-card__body) {
  padding: 14px 18px;
}

.home-case-card {
  border: 1px solid rgba(0, 56, 49, 0.08);
  box-shadow: 0 10px 24px rgba(0, 56, 49, 0.08);
}

.case-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
}

.case-id {
  margin: 0 0 6px;
  color: #0b6cff;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.case-head h3 {
  margin: 0;
  color: #03241f;
  font-size: 1.25rem;
  font-weight: 850;
}

.case-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 18px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.detail-grid-home {
  grid-template-columns: minmax(0, 1fr);
}

.home-case-shell {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 140px;
  gap: 14px;
  align-items: start;
}

.home-case-main {
  min-width: 0;
}

.home-case-head {
  margin-bottom: 10px;
}

.detail-item {
  padding: 14px;
  border-radius: 16px;
  background: rgba(0, 56, 49, 0.04);
  border: 1px solid rgba(0, 56, 49, 0.08);
}

.detail-label {
  display: block;
  margin-bottom: 6px;
  color: #4f6d67;
  font-size: 0.84rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.detail-item strong {
  color: #03241f;
  font-weight: 750;
  white-space: normal;
  overflow-wrap: anywhere;
}

.case-actions {
  display: grid;
  gap: 12px;
  align-content: start;
}

.case-actions :deep(.case-action-btn) {
  width: 100%;
  min-height: 44px;
  margin: 0;
  border-radius: 14px;
  font-weight: 700;
}

.case-actions :deep(.case-action-btn.action-assign) {
  border-color: #0b6cff;
  background: linear-gradient(135deg, #0b6cff 0%, #0057d8 100%);
  color: #ffffff;
}

.case-actions :deep(.case-action-btn.action-assign:hover),
.case-actions :deep(.case-action-btn.action-assign:focus-visible) {
  border-color: #0051c6;
  background: linear-gradient(135deg, #2a86ff 0%, #0b6cff 100%);
  color: #ffffff;
}

.case-actions :deep(.case-action-btn.action-hide) {
  border-color: #f0b8b2;
  background: #fff4f2;
  color: #b42318;
}

.case-actions :deep(.case-action-btn.action-hide:hover),
.case-actions :deep(.case-action-btn.action-hide:focus-visible) {
  border-color: #b42318;
  background: #b42318;
  color: #ffffff;
}

.case-actions :deep(.case-action-btn.action-details) {
  border-color: #003831;
  background: #003831;
  color: #ffffff;
}

.case-actions :deep(.case-action-btn.action-details:hover),
.case-actions :deep(.case-action-btn.action-details:focus-visible) {
  border-color: #018575;
  background: #018575;
  color: #ffffff;
}

.home-case-card .case-head {
  margin-bottom: 10px;
}

.home-case-card .case-id {
  margin-bottom: 0;
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(11, 108, 255, 0.14), rgba(11, 108, 255, 0.04));
  border: 1px solid rgba(11, 108, 255, 0.18);
  font-size: 1.16rem;
  line-height: 1;
  letter-spacing: 0.05em;
}

.home-meta-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 6px 0 0;
  align-items: stretch;
}

.home-meta-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  padding-bottom: 8px;
}

.home-meta-item:last-child {
  padding-bottom: 0;
}

.home-meta-label {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(0, 56, 49, 0.08);
  color: #48645d;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.home-meta-value {
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.3;
  color: #052b25;
  white-space: normal;
  overflow-wrap: break-word;
}

.home-meta-item-title .home-meta-value {
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.home-case-actions {
  gap: 7px;
  justify-items: stretch;
  align-self: start;
}

.home-case-actions :deep(.case-action-btn) {
  min-height: 32px;
  width: 100%;
  padding: 6px 10px;
  font-size: 0.78rem;
  border-radius: 11px;
}

.upload-trigger {
  width: 100%;
}

.upload-trigger :deep(.el-upload),
.upload-trigger :deep(.el-button) {
  width: 100%;
}

.status-editor {
  display: grid;
  gap: 10px;
}

@media (max-width: 980px) {
  .case-layout {
    grid-template-columns: 1fr;
  }

  .home-cards-grid {
    grid-template-columns: 1fr;
  }

  .home-case-shell {
    grid-template-columns: 1fr;
  }

  .home-case-actions {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .home-meta-row {
    gap: 10px;
  }

  .home-case-shell {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .home-case-actions {
    grid-template-columns: 1fr;
  }

  .case-head {
    flex-direction: column;
  }
}

.disabled-button-wrapper {
  width: 100%;
}

.disabled-button-wrapper :deep(.el-button) {
  width: 100%;
}
</style>
